import React, { useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import ReactApexChart from 'react-apexcharts'

import { httpOk, GET } from 'helpers/api'
import { formatMoney } from 'helpers/helpers'
import { buildAnalyticsUrl } from 'pages/main/analytics/parts/analyticsDates'
import ChainFilterBar, { useChainFilters } from '../parts/ChainFilterBar'
import { downloadExcel, periodText } from '../parts/chainExcel'
import {
	money, percent, columnStats, heatStyle, buildRanks, chainColor,
	RankBadge, FillCell, useSort, SortTh, ChainKpi, HeatLegend, ChainEmpty,
} from '../parts/chainUi'

// Строки ОПиУ: sign — как строка входит в прибыль; goodHigh — направление подсветки
const PNL_LINES = [
	{ key: 'revenue', percentKey: null, goodHigh: true, strong: true },
	{ key: 'cost', percentKey: 'cost_percent', goodHigh: false },
	{ key: 'gross_profit', percentKey: 'gross_margin', goodHigh: true, strong: true },
	{ key: 'write_offs', percentKey: 'write_offs_percent', goodHigh: false },
	{ key: 'operating_expenses', percentKey: 'operating_percent', goodHigh: false },
	{ key: 'staff_payments', percentKey: 'staff_percent', goodHigh: false },
	{ key: 'profit', percentKey: 'margin', goodHigh: true, strong: true },
]

function Profit() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [view, setView] = useState('rating')

	const filters = useChainFilters({}, searchWithFilter)

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/report/profit', filter), {}, { loader: false })
		if (httpOk(response)) setData(response.data)
	}

	const branches = useMemo(() => data?.branches || [], [data])
	const totals = data?.totals || {}
	const { sorted, sort, toggle } = useSort(branches, 'profit')
	const { ranks, count } = useMemo(() => buildRanks(branches, 'margin', true), [branches])

	const stats = useMemo(() => {
		const result = {}
		PNL_LINES.forEach(line => {
			result[line.key] = columnStats(branches, line.percentKey || line.key)
		})
		result.margin = columnStats(branches, 'margin')
		result.profit_abs = columnStats(branches, 'profit')
		return result
	}, [branches])

	const lineLabels = {
		revenue: t('revenue'),
		cost: t('chain_cogs'),
		gross_profit: t('chain_gross_profit'),
		write_offs: t('chain_write_offs'),
		operating_expenses: t('chain_operating_expenses'),
		staff_payments: t('chain_staff_payments'),
		profit: t('chain_profit'),
	}

	const losing = branches.filter(item => item.profit < 0)
	const withRevenue = branches.filter(item => item.revenue > 0)
	const bestMargin = withRevenue.length > 0 ? [...withRevenue].sort((a, b) => b.margin - a.margin)[0] : null
	const worstMargin = withRevenue.length > 1 ? [...withRevenue].sort((a, b) => a.margin - b.margin)[0] : null
	const maxProfit = Math.max(0, ...branches.map(item => Math.abs(Number(item.profit || 0))))

	function exportExcel() {
		if (!data) return
		const period = `${t('period')}: ${periodText(data.start_date, data.end_date)}`
		const ordered = [...branches].sort((a, b) => b.profit - a.profit)
		const marginRanks = buildRanks(branches, 'margin', true).ranks
		downloadExcel(`${t('chain_profit_title')} ${periodText(data.start_date, data.end_date)}`, [
			{
				name: t('chain_pnl'),
				title: t('chain_profit_title'),
				subtitle: period,
				columns: [
					{ label: t('chain_pnl_line'), type: 'text', width: 30 },
					...ordered.map(branch => ({ label: branch.pos_name, type: 'money' })),
					{ label: t('total'), type: 'money' },
				],
				rows: [
					...PNL_LINES.map(line => [lineLabels[line.key], ...ordered.map(branch => branch[line.key]), totals[line.key]]),
					[t('chain_margin_percent'), ...ordered.map(branch => branch.margin), totals.margin],
					[t('chain_supplier_payments_info'), ...ordered.map(branch => branch.supplier_payments), totals.supplier_payments],
				],
			},
			{
				name: t('chain_rating'),
				title: `${t('chain_profit_title')} — ${t('chain_rating')}`,
				subtitle: period,
				columns: [
					{ label: t('chain_rank'), type: 'int', width: 8 },
					{ label: t('chain_branch'), type: 'text', width: 30 },
					{ label: t('revenue'), type: 'money' },
					{ label: t('chain_cogs'), type: 'money' },
					{ label: t('chain_gross_profit'), type: 'money' },
					{ label: t('chain_write_offs'), type: 'money' },
					{ label: t('chain_operating_expenses'), type: 'money' },
					{ label: t('chain_staff_payments'), type: 'money' },
					{ label: t('chain_profit'), type: 'money' },
					{ label: t('chain_margin_percent'), type: 'percent', width: 10 },
					{ label: t('chain_supplier_payments_info'), type: 'money', width: 18 },
				],
				rows: ordered.map(branch => [
					marginRanks[branch.pos_id], branch.pos_name, branch.revenue, branch.cost, branch.gross_profit, branch.write_offs,
					branch.operating_expenses, branch.staff_payments, branch.profit, branch.margin, branch.supplier_payments,
				]),
				footer: ['', t('total'), totals.revenue, totals.cost, totals.gross_profit, totals.write_offs,
					totals.operating_expenses, totals.staff_payments, totals.profit, totals.margin, totals.supplier_payments],
			},
		])
	}

	return (
		<>
			<ChainFilterBar
				title={t('chain_profit_title')}
				icon="uil-chart-pie-alt"
				filters={filters}
				onExcel={exportExcel}
				excelDisabled={!data}
				tabs={[
					{ id: 'rating', label: t('chain_rating'), icon: 'uil-trophy' },
					{ id: 'pnl', label: t('chain_pnl'), icon: 'uil-list-ul' },
				]}
				activeTab={view}
				onTabChange={setView}
			/>

			<div className="chain-note mb-3">
				<i className="uil uil-info-circle"></i>
				<span>{t('chain_profit_note')}</span>
			</div>

			<div className="row g-3 mb-3">
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('revenue')} icon="uil-money-bill" color="#5b73e8" value={money(totals.revenue)} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_gross_profit')} icon="uil-chart-pie" color="#7c4dff"
						value={money(totals.gross_profit)}
						sub={<>{t('margin')} <b>{percent(totals.gross_margin)}</b></>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_all_expenses')} icon="uil-money-withdraw" color="#f06548"
						value={money(Number(totals.operating_expenses || 0) + Number(totals.staff_payments || 0) + Number(totals.write_offs || 0))}
						sub={<>{percent((Number(totals.operating_percent || 0) + Number(totals.staff_percent || 0) + Number(totals.write_offs_percent || 0)))} {t('chain_of_revenue')}</>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_profit')} icon="uil-wallet" color={Number(totals.profit) < 0 ? '#f06548' : '#12b886'}
						value={money(totals.profit)}
						sub={<>{t('chain_margin_percent')} <b>{percent(totals.margin)}</b></>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_best_margin')} icon="uil-trophy" color="#12b886"
						value={bestMargin ? bestMargin.pos_name : '—'}
						sub={bestMargin ? <>{percent(bestMargin.margin)} · {money(bestMargin.profit)}</> : null} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_losing_branches')} icon="uil-exclamation-triangle" color={losing.length > 0 ? '#f06548' : '#12b886'}
						value={formatMoney(losing.length)}
						sub={worstMargin ? <>{t('chain_worst')}: {worstMargin.pos_name} {percent(worstMargin.margin)}</> : null} />
				</div>
			</div>

			{view === 'rating' && (
				<div className="card chain-card mb-3">
					<div className="card-body">
						<div className="chain-toolbar">
							<div className="chain-toolbar-group">
								<h5 className="mb-0">{t('chain_rating')}</h5>
								<span className="chain-muted">{t('chain_rank_by_margin')}</span>
							</div>
							<HeatLegend />
						</div>
						<div className="chain-scroll">
							<table className="chain-table">
								<thead>
									<tr>
										<th className="chain-index chain-sticky">#</th>
										<SortTh sortKey="pos_name" sort={sort} onSort={toggle} className="chain-sticky-2">{t('chain_branch')}</SortTh>
										<SortTh sortKey="revenue" sort={sort} onSort={toggle} className="text-end">{t('revenue')}</SortTh>
										<SortTh sortKey="cost" sort={sort} onSort={toggle} className="text-end">{t('chain_cogs')}</SortTh>
										<SortTh sortKey="gross_profit" sort={sort} onSort={toggle} className="text-end">{t('chain_gross_profit')}</SortTh>
										<SortTh sortKey="write_offs" sort={sort} onSort={toggle} className="text-end">{t('chain_write_offs')}</SortTh>
										<SortTh sortKey="operating_expenses" sort={sort} onSort={toggle} className="text-end">{t('chain_operating_expenses')}</SortTh>
										<SortTh sortKey="staff_payments" sort={sort} onSort={toggle} className="text-end">{t('chain_staff_payments')}</SortTh>
										<SortTh sortKey="profit" sort={sort} onSort={toggle} className="text-end">{t('chain_profit')}</SortTh>
										<SortTh sortKey="margin" sort={sort} onSort={toggle} className="text-end">{t('chain_margin_percent')}</SortTh>
										<SortTh sortKey="supplier_payments" sort={sort} onSort={toggle} className="text-end" title={t('chain_supplier_payments_hint')}>{t('chain_supplier_payments_info')}</SortTh>
									</tr>
								</thead>
								<tbody>
									{sorted.length === 0 && <ChainEmpty colSpan={11} />}
									{sorted.map(item => (
										<tr key={item.pos_id}>
											<td className="chain-index chain-sticky"><RankBadge rank={ranks[item.pos_id]} count={count} /></td>
											<td className="chain-name chain-sticky-2">
												<span className="chain-dot" style={{ background: chainColor(branches.indexOf(item)) }}></span>
												{item.pos_name}
											</td>
											<td className="chain-num">{money(item.revenue)}</td>
											<PnlCell value={item.cost} share={item.cost_percent} style={heatStyle(item.cost_percent, stats.cost, false)} />
											<PnlCell value={item.gross_profit} share={item.gross_margin} style={heatStyle(item.gross_margin, stats.gross_profit)} />
											<PnlCell value={item.write_offs} share={item.write_offs_percent} style={heatStyle(item.write_offs_percent, stats.write_offs, false)} />
											<PnlCell value={item.operating_expenses} share={item.operating_percent} style={heatStyle(item.operating_percent, stats.operating_expenses, false)} />
											<PnlCell value={item.staff_payments} share={item.staff_percent} style={heatStyle(item.staff_percent, stats.staff_payments, false)} />
											<FillCell percent={maxProfit > 0 ? Math.abs(item.profit) / maxProfit * 100 : 0} tone={item.profit < 0 ? 'danger' : 'success'}>
												<b style={{ color: item.profit < 0 ? '#f06548' : undefined }}>{money(item.profit)}</b>
											</FillCell>
											<td className="chain-num" style={heatStyle(item.margin, stats.margin)}><b>{percent(item.margin)}</b></td>
											<td className="chain-num chain-muted">{money(item.supplier_payments)}</td>
										</tr>
									))}
								</tbody>
								{sorted.length > 0 && (
									<tfoot>
										<tr>
											<td className="chain-sticky"></td>
											<td className="chain-sticky-2">{t('total')}</td>
											<td className="chain-num">{money(totals.revenue)}</td>
											<PnlCell value={totals.cost} share={totals.cost_percent} />
											<PnlCell value={totals.gross_profit} share={totals.gross_margin} />
											<PnlCell value={totals.write_offs} share={totals.write_offs_percent} />
											<PnlCell value={totals.operating_expenses} share={totals.operating_percent} />
											<PnlCell value={totals.staff_payments} share={totals.staff_percent} />
											<td className="chain-num" style={{ color: Number(totals.profit) < 0 ? '#f06548' : undefined }}>{money(totals.profit)}</td>
											<td className="chain-num">{percent(totals.margin)}</td>
											<td className="chain-num">{money(totals.supplier_payments)}</td>
										</tr>
									</tfoot>
								)}
							</table>
						</div>
					</div>
				</div>
			)}

			{view === 'pnl' && (
				<div className="card chain-card mb-3">
					<div className="card-body">
						<div className="chain-toolbar">
							<h5 className="mb-0">{t('chain_pnl')}</h5>
							<HeatLegend />
						</div>
						<div className="chain-scroll">
							<table className="chain-table">
								<thead>
									<tr>
										<th className="chain-sticky" style={{ minWidth: '220px' }}>{t('chain_pnl_line')}</th>
										{branches.map((branch, index) => (
											<th key={branch.pos_id} className="text-end">
												<span className="chain-dot" style={{ background: chainColor(index) }}></span>
												{branch.pos_name}
											</th>
										))}
										<th className="text-end chain-total-col">{t('total')}</th>
									</tr>
								</thead>
								<tbody>
									{branches.length === 0 && <ChainEmpty colSpan={2} />}
									{branches.length > 0 && PNL_LINES.map(line => (
										<tr key={line.key}>
											<td className="chain-sticky" style={{ fontWeight: line.strong ? 700 : 400, paddingLeft: line.strong ? undefined : '22px' }}>
												{line.strong ? '' : '− '}{lineLabels[line.key]}
											</td>
											{branches.map(branch => (
												<td key={branch.pos_id} className="chain-num"
													style={line.key === 'revenue' ? undefined : heatStyle(line.percentKey ? branch[line.percentKey] : branch[line.key], stats[line.key], line.goodHigh)}>
													<span style={{ fontWeight: line.strong ? 700 : 400, color: line.key === 'profit' && branch.profit < 0 ? '#f06548' : undefined }}>
														{money(branch[line.key])}
													</span>
													{line.percentKey && <div className="chain-muted">{percent(branch[line.percentKey])}</div>}
												</td>
											))}
											<td className="chain-num chain-total-col">
												{money(totals[line.key])}
												{line.percentKey && <div className="chain-muted">{percent(totals[line.percentKey])}</div>}
											</td>
										</tr>
									))}
									{branches.length > 0 && (
										<tr>
											<td className="chain-sticky chain-muted" title={t('chain_supplier_payments_hint')}>{t('chain_supplier_payments_info')}</td>
											{branches.map(branch => (
												<td key={branch.pos_id} className="chain-num chain-muted">{money(branch.supplier_payments)}</td>
											))}
											<td className="chain-num chain-total-col chain-muted">{money(totals.supplier_payments)}</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			)}

			<ProfitChart branches={branches} />
		</>
	)
}

function PnlCell({ value, share, style }) {
	return (
		<td className="chain-num" style={style}>
			{money(value)}
			<div className="chain-muted">{percent(share)}</div>
		</td>
	)
}

// Прибыль филиалов столбиками: минус красным
function ProfitChart({ branches }) {
	const { t } = useTranslation()
	const rows = [...branches].sort((a, b) => b.profit - a.profit)
	const series = [
		{ name: t('chain_gross_profit'), data: rows.map(item => Math.round(item.gross_profit)) },
		{ name: t('chain_profit'), data: rows.map(item => Math.round(item.profit)) },
	]
	const options = {
		chart: { type: 'bar', toolbar: { show: false } },
		plotOptions: {
			bar: {
				columnWidth: '60%',
				borderRadius: 3,
				colors: { ranges: [{ from: -1e15, to: -0.5, color: '#f06548' }] },
			},
		},
		colors: ['#b4bff5', '#12b886'],
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: { categories: rows.map(item => item.pos_name), labels: { rotate: -30, hideOverlappingLabels: false, trim: true } },
		yaxis: { labels: { formatter: value => formatMoney(Math.round(value / 1000)) + 'k' } },
		tooltip: { shared: true, intersect: false, y: { formatter: value => formatMoney(Math.round(value)) } },
	}

	if (rows.length === 0) return null

	return (
		<div className="card chain-card mb-3">
			<div className="card-body">
				<div className="chain-card-title"><h5>{t('chain_profit_by_branches')}</h5></div>
				<ReactApexChart type="bar" height={340} series={series} options={options} />
			</div>
		</div>
	)
}

export default Profit
